import type { ObjectId } from 'mongodb';

import type { Entity } from './Entities';
import type { IsoDateTimeString } from './Helpers';
import type { ProgramType } from './ProgramTypes';

/**
 * Percentage-of-standard-payment per calendar month; 100 = 100% (no change).
 * Values are whole-integer percentages; the plan-save gate is responsible for rejecting
 * fractional values, which is what lets equivalence be an exact integer comparison.
 */
export interface MonthlyMultipliers {
  jan: number;
  feb: number;
  mar: number;
  apr: number;
  may: number;
  jun: number;
  jul: number;
  aug: number;
  sep: number;
  oct: number;
  nov: number;
  dec: number;
}

export const MONTHLY_MULTIPLIER_KEYS: ReadonlyArray<keyof MonthlyMultipliers> = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

/**
 * A plan is annual-equivalent when its multipliers average 100% (sum to 1200),
 * so any 12 consecutive months cost the same as the standard flat payment.
 */
export const isAnnualEquivalent = (multipliers: MonthlyMultipliers): boolean =>
  Math.round(MONTHLY_MULTIPLIER_KEYS.reduce((total, key) => total + multipliers[key], 0)) === 1200;

/** Recorded when an admin knowingly saves a plan whose annual total differs from standard. */
export interface PricingPlanException {
  acknowledgedBy: string;
  acknowledgedAt: IsoDateTimeString;
}

export enum PricingPlanStatus {
  active = 'active',
  archived = 'archived',
}

/**
 * One document per plan VERSION. Versions of the same plan share a planGroupId;
 * exactly one version per group has isCurrent: true. Published versions are immutable —
 * enrollments reference a version document id and rely on its contents never changing —
 * EXCEPT `status`, which is a lifecycle field that toggles when a plan is archived.
 */
export type PricingPlanDocument = Entity & {
  type: 'seasonal';
  planGroupId: ObjectId;
  name: string;
  programType: ProgramType;
  version: number;
  isCurrent: boolean;
  status: PricingPlanStatus;
  monthlyMultipliers: MonthlyMultipliers;
  /** Flat monthly fee in dollars, surfaced separately from the percentage schedule. */
  adminFeeMonthly?: number;
} & (
    | { annualEquivalent: true }
    | {
        annualEquivalent: false;
        /** Who acknowledged creating a non-equivalent plan, and when. */
        exception: PricingPlanException;
      }
  );

/**
 * Maps a plan group to a market scope, mirroring priceSheetMappings.
 * Omitted utility means the assignment covers the whole state for that org.
 */
export interface PricingPlanAssignmentDocument extends Entity {
  type: 'seasonal';
  planGroupId: ObjectId;
  organizationId: string;
  state: string;
  utility?: { lseId: number; name: string };
  programType: ProgramType;
}

/** One month of the customer's term-length schedule. Admin fee is surfaced separately from the base payment. */
export interface PricingPlanScheduleEntry {
  /** 1-based month sequence across the whole term. */
  monthNumber: number;
  /** 1-based contract (escalator) year this month falls in. */
  contractYear: number;
  /** Calendar month 1–12; drives the multiplier. */
  calendarMonth: number;
  /** Whole-integer percentage applied to the standard payment for this month; 100 = unchanged. */
  multiplier: number;
  baseAmount: number;
  adminFee: number;
  totalAmount: number;
}

/**
 * Immutable snapshot of the seasonal terms a customer signed, stored on the quote as `pricingPlan`.
 * Cloning the quote for a change order carries it forward; whatever changes the payments (e.g.
 * change-order creation) must recompute `schedule`.
 */
export interface SeasonalQuotePricingPlan {
  type: 'seasonal';
  planId: ObjectId;
  planGroupId: ObjectId;
  planVersion: number;
  monthlyMultipliers: MonthlyMultipliers;
  adminFeeMonthly?: number;
  /** Full term-length monthly schedule; source of truth for Concord transmission and reporting. */
  schedule: PricingPlanScheduleEntry[];
}

/** Discriminated by `type`; seasonal is the only plan type today. */
export type QuotePricingPlan = SeasonalQuotePricingPlan;

/** API-facing shape of `QuotePricingPlan`: ObjectIds serialize as strings. */
export type QuotePricingPlanResponse = Omit<QuotePricingPlan, 'planId' | 'planGroupId'> & {
  planId: string;
  planGroupId: string;
};
